// src/features/admin/api.ts (신규 파일)

import api from '../../shared/api/axios';

export type AdminCompany = {
    id: string;
    name: string;
    status: 'ACTIVE' | 'BLOCKED';
    createdAt: string;
    updatedAt: string;
    // ✅ company admin들 (백엔드 listCompanies 확장 응답)
    members?: {
        joinedAt: string;
        role: { name: string };
        user: AdminUser;
    }[];
    // ✅ 회사가 소유한 프로젝트들
    projects?: {
        id: string;
        name: string;
        status: 'DRAFT' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
        code?: string | null;
        createdAt: string;
        updatedAt: string;
        archivedAt?: string | null;
    }[];
};

export type AdminUser = {
    id: string;
    name: string;
    email: string;
    status: 'ACTIVE' | 'LOCKED';
    createdAt: string;
    updatedAt: string;
    // ✅ 백엔드 listUsers 확장 응답
    companyMemberships?: {
        id: string; // CompanyMember id
        companyId: string;
        joinedAt: string;
        company: {
            id: string;
            name: string;
            status: string;
        };
        role: {
            id: string;
            name: string;
            scope: RoleScope; // "COMPANY"
        };
    }[];
};

export type RoleScope = 'PROJECT' | 'COMPANY' | 'SERVICE';

export type Permission = {
    id: string;
    key: string;
    description?: string | null;
    category?: string | null;
    createdAt: string;
    updatedAt: string;
};

export type RoleGrant = {
    id: string;
    permissionId: string;
    permission: Permission;
};

export type Role = {
    id: string;
    scope: RoleScope;
    name: string;
    description?: string | null;
    createdAt: string;
    updatedAt: string;
    grants: RoleGrant[];
};

// -------- Company --------
export async function listCompanies(): Promise<AdminCompany[]> {
    const { data } = await api.get('/admin/companies');
    return data.data;
}

export async function createCompany(body: { name: string }): Promise<AdminCompany> {
    const { data } = await api.post('/admin/companies', body);
    return data.data;
}

export async function updateCompany(
    id: string,
    body: Partial<Pick<AdminCompany, 'name' | 'status'>>,
): Promise<AdminCompany> {
    const { data } = await api.patch(`/admin/companies/${id}`, body);
    return data.data;
}

export async function deleteCompany(id: string): Promise<void> {
    await api.delete(`/admin/companies/${id}`);
}

export async function createCompanyAdmin(
    companyId: string,
    params: { email: string; name?: string; password?: string },
) {
    const { data } = await api.post(`/admin/companies/${companyId}/admin`, params);
    return data.data;
}

// -------- Users --------
export async function listUsers(): Promise<AdminUser[]> {
    const { data } = await api.get('/admin/users');
    return data.data;
}

export async function updateUser(id: string, body: Partial<Pick<AdminUser, 'name' | 'status'>>): Promise<AdminUser> {
    const { data } = await api.patch(`/admin/users/${id}`, body);
    return data.data;
}

export async function deleteUser(id: string): Promise<void> {
    await api.delete(`/admin/users/${id}`);
}

// --------- Permissions ---------
export async function listPermissions(): Promise<Permission[]> {
    const { data } = await api.get('/admin/permissions');
    return data.data;
}

export async function createPermission(body: {
    key: string;
    description?: string;
    category?: string;
}): Promise<Permission> {
    const { data } = await api.post('/admin/permissions', body);
    return data.data;
}

export async function updatePermission(
    id: string,
    body: Partial<Pick<Permission, 'key' | 'description' | 'category'>>,
): Promise<Permission> {
    const { data } = await api.patch(`/admin/permissions/${id}`, body);
    return data.data;
}

export async function deletePermission(id: string): Promise<void> {
    await api.delete(`/admin/permissions/${id}`);
}

// -------- Roles --------
export async function listRoles(): Promise<Role[]> {
    const { data } = await api.get('/admin/roles');
    return data.data;
}

export async function createRole(body: {
    scope: RoleScope;
    name: string;
    description?: string;
    permissionId?: string[];
}): Promise<Role> {
    const res = await api.post('/admin/roles', body);
    return res.data.data;
}

export async function updateRole(
    id: string,
    body: Partial<{ name: string; description: string; permissionIds: string[] }>,
): Promise<Role> {
    const { data } = await api.patch(`/admin/roles/${id}`, body);
    return data.data;
}

export async function setRolePermissions(id: string, permissionIds: string[]): Promise<Role> {
    const { data } = await api.put(`/admin/roles/${id}/permissions`, { permissionIds });
    return data.data;
}

export async function deleteRole(id: string): Promise<void> {
    await api.delete(`/admin/roles/${id}`);
}
